import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box, Typography, Paper, Chip, List, ListItem, ListItemText,
  ListItemIcon, Divider, CircularProgress,
} from '@mui/material';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import MeetingRoomIcon from '@mui/icons-material/MeetingRoom';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import BlockIcon from '@mui/icons-material/Block';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import StarIcon from '@mui/icons-material/Star';
import { useTranslation } from 'react-i18next';

interface CardScanEvent {
  cardSN: string;
  doorId: number;
  readId: number;
  passStatus: number;
  passTime: string;
  receivedAt: string;
  deviceKey: string;
  direction: number;
  type: string;
}

interface DoorConfig {
  [key: string]: { name: string; type: string };
}

export interface CardScanEntryResult {
  status: 'checking' | 'allowed' | 'denied' | 'error' | 'not_found' | 'door_opened' | 'door_failed' | 'cooldown' | 'master';
  message?: string;
  cardLabel?: string;
  entryMode?: string;
  remainingRights?: number;
  totalRights?: number;
  usedRights?: number;
  depositAmount?: number;
}

interface AccessControlLivePanelProps {
  localServiceUrl: string;
  onNewCardScan?: (event: CardScanEvent) => void;
  entryResults?: Record<string, CardScanEntryResult>;
}

export type { CardScanEvent, DoorConfig };

const POLL_INTERVAL_MS = 2000;
const FAILURE_THRESHOLD = 3;

const AccessControlLivePanel: React.FC<AccessControlLivePanelProps> = ({
  localServiceUrl,
  onNewCardScan,
  entryResults: propEntryResults = {},
}) => {
  const { t } = useTranslation('salesSystem');
  const [events, setEvents] = useState<CardScanEvent[]>([]);
  const [lsEntryResults, setLsEntryResults] = useState<Record<string, CardScanEntryResult>>({});
  const entryResults = { ...lsEntryResults, ...propEntryResults };
  const [serviceReachable, setServiceReachable] = useState<boolean | null>(null);
  const [deviceConnected, setDeviceConnected] = useState(false);
  const [deviceListening, setDeviceListening] = useState(false);
  const [doorConfig, setDoorConfig] = useState<DoorConfig>({});
  const pollTimerRef = useRef<NodeJS.Timeout | null>(null);
  const lastEventIdRef = useRef<string>('');
  const consecutiveFailuresRef = useRef(0);
  const abortControllerRef = useRef<AbortController | null>(null);
  const notifiedEventsRef = useRef<Set<string>>(new Set());
  const onNewCardScanRef = useRef(onNewCardScan);
  onNewCardScanRef.current = onNewCardScan;

  const fetchLatestEvents = useCallback(async () => {
    if (!localServiceUrl) return;

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    try {
      const res = await fetch(
        `${localServiceUrl}/api/access-control/latest-events`,
        { signal: abortControllerRef.current.signal }
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const data = await res.json();
      if (data.success) {
        consecutiveFailuresRef.current = 0;

        const newEvents: CardScanEvent[] = data.events || [];
        const newFirstId = newEvents.length > 0
          ? `${newEvents[0].cardSN}-${newEvents[0].receivedAt}`
          : '';

        if (newFirstId !== lastEventIdRef.current) {
          setEvents(newEvents);
          lastEventIdRef.current = newFirstId;

          if (onNewCardScanRef.current) {
            for (const ev of newEvents) {
              const evKey = `${ev.cardSN}-${ev.receivedAt}`;
              if (!notifiedEventsRef.current.has(evKey)) {
                notifiedEventsRef.current.add(evKey);
                onNewCardScanRef.current(ev);
              }
            }
            if (notifiedEventsRef.current.size > 100) {
              const entries = Array.from(notifiedEventsRef.current);
              notifiedEventsRef.current = new Set(entries.slice(-50));
            }
          }
        }

        setServiceReachable(true);
        setDeviceConnected(data.connected ?? false);
        setDeviceListening(data.listening ?? false);
        if (data.doorConfig) setDoorConfig(data.doorConfig);
        if (data.entryResults) setLsEntryResults(data.entryResults);
      }
    } catch (err: any) {
      if (err.name === 'AbortError') return;

      consecutiveFailuresRef.current++;
      if (consecutiveFailuresRef.current >= FAILURE_THRESHOLD) {
        setServiceReachable(false);
        setDeviceConnected(false);
        setDeviceListening(false);
      }
    }
  }, [localServiceUrl]);

  useEffect(() => {
    fetchLatestEvents();
    pollTimerRef.current = setInterval(fetchLatestEvents, POLL_INTERVAL_MS);

    return () => {
      if (pollTimerRef.current) {
        clearInterval(pollTimerRef.current);
        pollTimerRef.current = null;
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [fetchLatestEvents]);

  const getDoorName = (doorId: number): string => {
    const cfg = doorConfig[String(doorId)];
    return cfg?.name || t('quickSaleScreen.accessControlLivePanel.doorWithId', { doorId });
  };

  const getDoorType = (doorId: number): string => {
    const cfg = doorConfig[String(doorId)];
    return cfg?.type || 'unknown';
  };

  const formatTime = (timeStr: string): string => {
    if (!timeStr) return '';
    try {
      const d = new Date(timeStr);
      if (isNaN(d.getTime())) return timeStr;
      return d.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
    } catch {
      return timeStr;
    }
  };

  const getStatusColor = (): string => {
    if (serviceReachable === null) return 'grey.400';
    if (!serviceReachable) return '#f44336';
    if (deviceConnected && deviceListening) return '#4caf50';
    if (deviceConnected) return '#ff9800';
    return '#ff9800';
  };

  const getStatusText = (): string => {
    if (serviceReachable === null) return t('quickSaleScreen.accessControlLivePanel.status.connecting');
    if (!serviceReachable) return t('quickSaleScreen.accessControlLivePanel.status.serviceUnavailable');
    if (deviceConnected && deviceListening) return t('quickSaleScreen.accessControlLivePanel.status.live');
    if (deviceConnected) return t('quickSaleScreen.accessControlLivePanel.status.notListening');
    return t('quickSaleScreen.accessControlLivePanel.status.noDevice');
  };

  return (
    <Paper
      elevation={3}
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        borderRadius: 2,
        bgcolor: 'background.paper',
      }}
    >
      {/* Header */}
      <Box sx={{
        px: 1.5, py: 1,
        bgcolor: 'primary.main',
        color: 'white',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        minHeight: 40,
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <MeetingRoomIcon sx={{ fontSize: 18 }} />
          <Typography variant="subtitle2" fontWeight={700} sx={{ fontSize: '0.75rem' }}>
            {t('quickSaleScreen.accessControlLivePanel.title')}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <FiberManualRecordIcon sx={{ fontSize: 10, color: getStatusColor() }} />
          <Typography variant="caption" sx={{ fontSize: '0.65rem', opacity: 0.9 }}>
            {getStatusText()}
          </Typography>
        </Box>
      </Box>

      {/* Content */}
      <Box sx={{ flex: 1, overflow: 'auto', px: 0.5, py: 0.5 }}>
        {serviceReachable === null ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <CircularProgress size={24} />
          </Box>
        ) : events.length === 0 ? (
          <Box sx={{
            display: 'flex', flexDirection: 'column', alignItems: 'center',
            justifyContent: 'center', height: '100%', opacity: 0.5, gap: 1,
          }}>
            <CreditCardIcon sx={{ fontSize: 32 }} />
            <Typography variant="caption" sx={{ fontSize: '0.7rem', textAlign: 'center' }}>
              {serviceReachable
                ? t('quickSaleScreen.accessControlLivePanel.waitingForCard')
                : t('quickSaleScreen.accessControlLivePanel.waitingForService')}
            </Typography>
          </Box>
        ) : (
          <List dense disablePadding>
            {events.map((ev, idx) => {
              const doorType = getDoorType(ev.doorId);
              const isEntry = doorType === 'entry';
              const evKey = `${ev.cardSN}-${ev.receivedAt}`;
              const result = entryResults[evKey];

              const getResultIcon = () => {
                if (!result) return null;
                switch (result.status) {
                  case 'checking': return <HourglassEmptyIcon sx={{ fontSize: 14, color: 'warning.main' }} />;
                  case 'cooldown': return <HourglassEmptyIcon sx={{ fontSize: 14, color: 'warning.main' }} />;
                  case 'master': return <StarIcon sx={{ fontSize: 14, color: '#ff9800' }} />;
                  case 'allowed':
                  case 'door_opened': return <CheckCircleOutlineIcon sx={{ fontSize: 14, color: 'success.main' }} />;
                  case 'denied':
                  case 'not_found': return <BlockIcon sx={{ fontSize: 14, color: 'error.main' }} />;
                  case 'error':
                  case 'door_failed': return <ErrorOutlineIcon sx={{ fontSize: 14, color: 'error.main' }} />;
                  default: return null;
                }
              };

              const getResultBgColor = () => {
                if (!result) return idx === 0 ? 'action.selected' : 'transparent';
                switch (result.status) {
                  case 'master': return 'rgba(255, 152, 0, 0.12)';
                  case 'allowed':
                  case 'door_opened': return 'rgba(76, 175, 80, 0.08)';
                  case 'denied':
                  case 'not_found': return 'rgba(244, 67, 54, 0.08)';
                  case 'error':
                  case 'door_failed': return 'rgba(244, 67, 54, 0.05)';
                  default: return idx === 0 ? 'action.selected' : 'transparent';
                }
              };

              return (
                <React.Fragment key={`${ev.cardSN}-${ev.receivedAt}-${idx}`}>
                  {idx > 0 && <Divider component="li" />}
                  <ListItem
                    sx={{
                      px: 0.5, py: 0.5,
                      bgcolor: getResultBgColor(),
                      borderRadius: 1,
                      mb: 0.25,
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 28 }}>
                      <CreditCardIcon
                        sx={{
                          fontSize: 16,
                          color: isEntry ? 'success.main' : 'info.main',
                        }}
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Typography
                            variant="body2"
                            fontWeight={idx === 0 ? 700 : 500}
                            sx={{ fontSize: '0.75rem', fontFamily: 'monospace' }}
                          >
                            {ev.cardSN}
                          </Typography>
                          {result?.cardLabel && (
                            <Typography variant="caption" sx={{ fontSize: '0.6rem', color: 'text.secondary' }}>
                              ({result.cardLabel})
                            </Typography>
                          )}
                          {getResultIcon()}
                        </Box>
                      }
                      secondary={
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, mt: 0.25 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Chip
                              label={isEntry
                                ? t('quickSaleScreen.accessControlLivePanel.direction.entry')
                                : t('quickSaleScreen.accessControlLivePanel.direction.exit')}
                              size="small"
                              color={isEntry ? 'success' : 'info'}
                              variant="outlined"
                              sx={{ height: 16, fontSize: '0.6rem', '& .MuiChip-label': { px: 0.5 } }}
                            />
                            <Typography variant="caption" sx={{ fontSize: '0.6rem', color: 'text.secondary' }}>
                              {getDoorName(ev.doorId)}
                            </Typography>
                            <Typography variant="caption" sx={{ fontSize: '0.6rem', color: 'text.secondary', ml: 'auto' }}>
                              {formatTime(ev.receivedAt)}
                            </Typography>
                          </Box>
                          {result?.message && (
                            <Typography
                              variant="caption"
                              sx={{
                                fontSize: '0.6rem',
                                color: result.status === 'master'
                                  ? '#ff9800'
                                  : result.status === 'allowed' || result.status === 'door_opened'
                                    ? 'success.main'
                                    : result.status === 'cooldown'
                                      ? 'warning.main'
                                      : result.status === 'denied' || result.status === 'not_found' || result.status === 'error' || result.status === 'door_failed'
                                        ? 'error.main'
                                        : 'text.secondary',
                                fontWeight: 500,
                              }}
                            >
                              {result.message}
                            </Typography>
                          )}
                          {result && result.status === 'master' && (
                            <Chip
                              label={t('quickSaleScreen.accessControlLivePanel.masterCard')}
                              size="small"
                              icon={<StarIcon sx={{ fontSize: '0.65rem !important' }} />}
                              sx={{ height: 16, fontSize: '0.55rem', '& .MuiChip-label': { px: 0.4 }, bgcolor: 'rgba(255, 152, 0, 0.15)', color: '#e65100', fontWeight: 700 }}
                            />
                          )}
                          {result && result.entryMode && result.status !== 'master' && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Chip
                                label={result.entryMode === 'SINGLE_ENTRY'
                                  ? t('quickSaleScreen.accessControlLivePanel.entryMode.singleEntry')
                                  : t('quickSaleScreen.accessControlLivePanel.entryMode.unlimited')}
                                size="small"
                                variant="outlined"
                                color={result.entryMode === 'SINGLE_ENTRY' ? 'warning' : 'primary'}
                                sx={{ height: 14, fontSize: '0.55rem', '& .MuiChip-label': { px: 0.4 } }}
                              />
                              {result.totalRights != null && (
                                <Typography variant="caption" sx={{ fontSize: '0.55rem', color: 'text.secondary' }}>
                                  {t('quickSaleScreen.accessControlLivePanel.rightsSummary', {
                                    used: result.usedRights,
                                    total: result.totalRights,
                                  })}
                                </Typography>
                              )}
                            </Box>
                          )}
                        </Box>
                      }
                      secondaryTypographyProps={{ component: 'div' }}
                    />
                  </ListItem>
                </React.Fragment>
              );
            })}
          </List>
        )}
      </Box>

      {/* Footer */}
      <Box sx={{
        px: 1, py: 0.5,
        borderTop: '1px solid',
        borderColor: 'divider',
        display: 'flex',
        justifyContent: 'center',
      }}>
        <Typography variant="caption" sx={{ fontSize: '0.6rem', color: 'text.secondary' }}>
          {t('quickSaleScreen.accessControlLivePanel.lastNEvents', { count: events.length })}
        </Typography>
      </Box>
    </Paper>
  );
};

export default AccessControlLivePanel;
